import { DashboardHeader } from '@/components/dashboard/dashboard-header'
import { DashboardShell } from '@/components/dashboard/dashboard-shell'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { envWeb } from '@/lib/env-web'
import { useTRPC } from '@/trpc/react'
import { getDisplayEmail, getDisplayName } from '@repo/shared_utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { AdvancedMarker, APIProvider, Map } from '@vis.gl/react-google-maps'
import { ChevronDown, MapPin, Pencil, Plus, Trash2 } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/dashboard/admin/super_admin/location-spoof-whitelist/')({
  component: RouteComponent,
})

function RouteComponent() {
  const api = useTRPC()
  const queryClient = useQueryClient()
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false)
  const [isUserPopoverOpen, setIsUserPopoverOpen] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [locationPopoverUserId, setLocationPopoverUserId] = useState<string | null>(null)
  const [editLat, setEditLat] = useState<string>('')
  const [editLng, setEditLng] = useState<string>('')
  const [editName, setEditName] = useState<string>('')

  const { data: whitelist, isPending: isWhitelistPending } = useQuery({
    ...api.superAdmin.getLocationSpoofWhitelist.queryOptions(),
  })

  const { data: allUsers } = useQuery({
    ...api.superAdmin.getAllUsers.queryOptions(),
    enabled: isAddDialogOpen,
  })

  const whitelistedUserIds = new Set(whitelist?.map((w) => w.userId) ?? [])
  const usersNotInWhitelist = allUsers?.filter((u) => !whitelistedUserIds.has(u.userId)) ?? []

  const { mutate: addUser, isPending: isAddPending } = useMutation({
    ...api.superAdmin.addUserToLocationSpoofWhitelist.mutationOptions(),
    onSuccess: () => {
      toast.success('User added to location spoof whitelist')
      queryClient.invalidateQueries({
        queryKey: api.superAdmin.getLocationSpoofWhitelist.queryKey(),
      })
      setIsAddDialogOpen(false)
      setSelectedUserId(null)
      setIsUserPopoverOpen(false)
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const { mutate: removeUser, isPending: isRemovePending } = useMutation({
    ...api.superAdmin.removeUserFromLocationSpoofWhitelist.mutationOptions(),
    onSuccess: () => {
      toast.success('User removed from location spoof whitelist')
      queryClient.invalidateQueries({
        queryKey: api.superAdmin.getLocationSpoofWhitelist.queryKey(),
      })
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  const { mutate: updateLocation, isPending: isUpdateLocationPending } = useMutation({
    ...api.superAdmin.updateUserSpoofedLocation.mutationOptions(),
    onSuccess: () => {
      toast.success('Location updated')
      queryClient.invalidateQueries({
        queryKey: api.superAdmin.getLocationSpoofWhitelist.queryKey(),
      })
      setLocationPopoverUserId(null)
    },
    onError: (error) => {
      toast.error(error.message)
    },
  })

  function handleAddUser() {
    if (!selectedUserId) {
      toast.error('Please select a user')
      return
    }
    addUser({ userId: selectedUserId })
  }

  if (isWhitelistPending)
    return (
      <DashboardShell>
        <DashboardHeader heading="Location Spoof Whitelist" />
        <Skeleton className="h-40 w-full" />
      </DashboardShell>
    )

  return (
    <DashboardShell>
      <DashboardHeader heading="Location Spoof Whitelist" />
      <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-200 mb-6">
        <span className="font-medium">Super Admin View:</span> Add users and set their spoofed
        location via the location column. Whitelisted users use that location instead of GPS in the
        employee dashboard (clock in/out, map).
      </div>

      <div className="flex justify-between items-center mb-4">
        <h2 className="text-lg font-semibold">Whitelisted Users ({whitelist?.length ?? 0})</h2>
        <Dialog
          open={isAddDialogOpen}
          onOpenChange={(open) => {
            setIsAddDialogOpen(open)
            if (!open) {
              setSelectedUserId(null)
              setIsUserPopoverOpen(false)
            }
          }}
        >
          <DialogTrigger asChild>
            <Button>
              <Plus className="mr-2 h-4 w-4" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add User to Whitelist</DialogTitle>
              <DialogDescription>
                Select a user to allow them to set a default location on their profile for use in
                the employee dashboard.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">User</label>
                <Popover open={isUserPopoverOpen} onOpenChange={setIsUserPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={isUserPopoverOpen}
                      className="w-full justify-between font-normal"
                    >
                      {selectedUserId ? (
                        (() => {
                          const u = usersNotInWhitelist.find((x) => x.userId === selectedUserId)
                          return u
                            ? `${getDisplayName(u.name ?? '', u.maskName ?? false)} (${getDisplayEmail(u.email ?? '', false)})`
                            : selectedUserId
                        })()
                      ) : (
                        <span className="text-muted-foreground">Search users...</span>
                      )}
                      <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="w-[var(--radix-popover-trigger-width)] p-0"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Search by name or email..." />
                      <CommandList>
                        <CommandEmpty>No users found.</CommandEmpty>
                        <CommandGroup>
                          {usersNotInWhitelist.map((user) => (
                            <CommandItem
                              key={user.userId}
                              value={`${getDisplayName(user.name ?? '', user.maskName ?? false)} ${getDisplayEmail(user.email ?? '', false)} ${user.userId}`}
                              onSelect={() => {
                                setSelectedUserId(user.userId)
                                setIsUserPopoverOpen(false)
                              }}
                            >
                              {getDisplayName(user.name ?? '', user.maskName ?? false)} (
                              {getDisplayEmail(user.email ?? '', false)})
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleAddUser} disabled={!selectedUserId || isAddPending}>
                {isAddPending ? 'Adding...' : 'Add'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {whitelist && whitelist.length > 0 ? (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Business</TableHead>
                <TableHead>Location</TableHead>
                <TableHead className="w-[80px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {whitelist.map((entry) => (
                <TableRow key={entry.userId}>
                  <TableCell>
                    {entry.user
                      ? getDisplayName(entry.user.name ?? '', entry.user.maskName ?? false)
                      : entry.userId}
                  </TableCell>
                  <TableCell>
                    {entry.user ? getDisplayEmail(entry.user.email ?? '', false) : '--'}
                  </TableCell>
                  <TableCell>
                    {entry.user?.business?.name ?? entry.user?.businessId ?? '--'}
                  </TableCell>
                  <TableCell>
                    <Popover
                      open={locationPopoverUserId === entry.userId}
                      onOpenChange={(open) => {
                        if (open) {
                          setLocationPopoverUserId(entry.userId)
                          setEditLat(entry.latitude ?? '')
                          setEditLng(entry.longitude ?? '')
                          setEditName(entry.locationName ?? '')
                        } else {
                          setLocationPopoverUserId(null)
                        }
                      }}
                    >
                      <PopoverTrigger asChild>
                        <button
                          type="button"
                          className="flex items-center gap-1 text-sm w-full text-left hover:bg-muted/50 rounded px-2 py-1 -mx-2 -my-1 transition-colors cursor-pointer min-h-[2rem]"
                        >
                          {entry.latitude && entry.longitude ? (
                            <>
                              <MapPin className="h-3 w-3 shrink-0" />
                              <span className="truncate">
                                {entry.locationName ?? `${entry.latitude}, ${entry.longitude}`}
                              </span>
                              <Pencil className="h-3 w-3 shrink-0 ml-auto opacity-50" />
                            </>
                          ) : (
                            <>
                              <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
                              <span className="text-muted-foreground">Click to set location</span>
                            </>
                          )}
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[480px] p-0" align="start">
                        <div className="p-4 space-y-4">
                          <div className="space-y-2">
                            <Label>Location Name (optional)</Label>
                            <Input
                              value={editName}
                              onChange={(e) => setEditName(e.target.value)}
                              placeholder="e.g., Main Office"
                            />
                          </div>
                          <div className="space-y-2">
                            <div className="flex justify-between items-center">
                              <Label>Click on map to set coordinates</Label>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditLat('')
                                  setEditLng('')
                                }}
                              >
                                Clear
                              </Button>
                            </div>
                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                              <div>Latitude: {editLat || 'Not set'}</div>
                              <div>Longitude: {editLng || 'Not set'}</div>
                            </div>
                            <div className="border rounded-lg overflow-hidden">
                              <APIProvider apiKey={envWeb.VITE_PUBLIC_MAPS_API_KEY}>
                                <Map
                                  style={{ width: '100%', height: '300px' }}
                                  defaultCenter={{
                                    lat: editLat
                                      ? Number(editLat)
                                      : entry.latitude
                                        ? Number(entry.latitude)
                                        : 14.5547,
                                    lng: editLng
                                      ? Number(editLng)
                                      : entry.longitude
                                        ? Number(entry.longitude)
                                        : 121.0244,
                                  }}
                                  defaultZoom={15}
                                  gestureHandling={'cooperative'}
                                  disableDefaultUI={false}
                                  mapId={`location-spoof-map-${entry.userId}`}
                                  onClick={(e) => {
                                    if (e.detail.latLng) {
                                      setEditLat(e.detail.latLng.lat.toString())
                                      setEditLng(e.detail.latLng.lng.toString())
                                    }
                                  }}
                                >
                                  {editLat && editLng && (
                                    <AdvancedMarker
                                      position={{
                                        lat: Number(editLat),
                                        lng: Number(editLng),
                                      }}
                                    />
                                  )}
                                </Map>
                              </APIProvider>
                            </div>
                          </div>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setLocationPopoverUserId(null)}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              disabled={
                                !editLat ||
                                !editLng ||
                                Number.isNaN(Number(editLat)) ||
                                Number.isNaN(Number(editLng)) ||
                                isUpdateLocationPending
                              }
                              onClick={() => {
                                updateLocation({
                                  userId: entry.userId,
                                  latitude: Number(editLat),
                                  longitude: Number(editLng),
                                  ...(editName.trim() ? { locationName: editName.trim() } : {}),
                                })
                              }}
                            >
                              {isUpdateLocationPending ? 'Saving...' : 'Save'}
                            </Button>
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeUser({ userId: entry.userId })}
                      disabled={isRemovePending}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="rounded-md border p-8 text-center text-muted-foreground">
          No users in the whitelist yet. Click &quot;Add User&quot; to get started.
        </div>
      )}
    </DashboardShell>
  )
}
